import sessionTokenStorage from "@/constants/SessionTokenStorage";
import * as Sentry from "@sentry/react-native";
import type { AxiosInstance } from "axios";
import { Platform } from "react-native";

/**
 * Generic function to configure the token interceptor.
 * @param apiInstance AxiosInstance for the specific microservice.
 */
export const configureTokenInterceptor = (apiInstance: AxiosInstance) => {
  apiInstance.interceptors.request.use(
    async (config) => {
      console.groupCollapsed("%cRequest Details", requestLogTitleStyle);
      console.log("[Request Interceptor] Outgoing request:");
      console.log("URL:", config.url);
      console.log("Method:", config.method);
      console.log("Headers:", config.headers);
      console.log("Params:", config.params);
      console.log("Data:", config.params);
      console.dir(config.data, { depth: Number.POSITIVE_INFINITY });
      console.groupEnd();

      if (!config.url?.includes("/welcome")) {
        const token = sessionTokenStorage.getToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};

export const configureSentryResponseInterceptor = (apiInstance: AxiosInstance) => {
  apiInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response) {
        console.error("API Error (Response):", error.response.data);
        console.error("Status:", error.response.status);
        console.error("Headers:", error.response.headers);

        Sentry.captureException(error, {
          extra: {
            type: "axios_error_response",
            method: error.config.method,
            url: error.config.url,
            request_data: error.config.data,
            response_status: error.response.status,
            response_data: error.response.data,
            response_headers: error.response.headers,
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
      } else if (error.request) {
        console.error("API Error (Request): No response received.", error.request);
        Sentry.captureException(error, {
          extra: {
            type: "axios_error_no_response",
            method: error.config.method,
            url: error.config.url,
            request_data: error.config.data,
            request_details: error.request,
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
      } else {
        console.error("API Error (Setup):", error.message);
        Sentry.captureException(error, {
          extra: {
            type: "axios_error_setup",
            method: error.config?.method,
            url: error.config?.url,
            message: error.message,
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
      }

      return Promise.reject(error);
    },
  );
};

const requestLogTitleStyle = [
  "color: #A78BFA", // violet-400
  "font-weight: bold",
].join(";");
